'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import styles from './Hero.module.scss'
import { ComponentProps } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeSvg } from '@/lib/utils/html-sanitizer'
import { scrollToWithLenis } from '@/components/LenisProvider'



// Vimeo Player API types
interface VimeoPlayer {
  on: (event: string, callback: () => void) => void
  off: (event: string, callback: () => void) => void
  destroy: () => void
  ready: () => Promise<void>
  play: () => Promise<void>
}

interface HeroProps extends ComponentProps {
  vimeoId?: string
  svgCode?: string
  // Legacy support
  desktopVimeoId?: string
  desktopVideo?: string
}

export const Hero: React.FC<HeroProps> = ({
  vimeoId = '',
  svgCode = '<svg viewBox="0 0 100 100"><rect width="100" height="100" fill="#007bff"/></svg>',
  // Legacy support
  desktopVimeoId = '',
  desktopVideo = ''
}) => {
  const [showSvg, setShowSvg] = useState(false)
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()

  // Vimeo player refs
  const playerRef = useRef<VimeoPlayer | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Video loading states
  const [isVimeoAPILoaded, setIsVimeoAPILoaded] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)

  // Parse Vimeo ID and hash parameter for unlisted videos
  const parseVimeoId = (id: string) => {
    if (!id) return { videoId: '', hashParam: '' }
    
    const trimmedId = id.trim()
    const [videoId, hashParam] = trimmedId.split('?h=')
    
    return {
      videoId: videoId || '',
      hashParam: hashParam || ''
    }
  }
  
  // Use new vimeoId prop, fallback to legacy props for backward compatibility
  const finalVimeoId = vimeoId || desktopVimeoId || desktopVideo || ''
  const vimeoData = parseVimeoId(finalVimeoId)
  const activeVimeoId = vimeoData.videoId
  

  
  // Check if video should be visible (loaded, playing)
  const shouldShowVideo = isAdminMode || (videoLoaded && videoPlaying)

  // Load Vimeo Player API
  const loadVimeoAPI = useCallback(() => {
    if ((window as unknown as { Vimeo?: unknown }).Vimeo || isVimeoAPILoaded) {
      setIsVimeoAPILoaded(true)
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://player.vimeo.com/api/player.js'
      script.onload = () => {
        setIsVimeoAPILoaded(true)
        resolve()
      }
      document.head.appendChild(script)
    })
  }, [isVimeoAPILoaded])

  // Initialize Vimeo Player
  const initializeVimeoPlayer = useCallback(async () => {
    if (!containerRef.current || !isVimeoAPILoaded || playerRef.current || !activeVimeoId) {
      return
    }

    try {
      await loadVimeoAPI()
      
      // Build player options - use URL for unlisted videos with hash, separate options for background settings
      const playerOptions: Record<string, unknown> = {
        responsive: false,
        width: 1920,
        height: 1080,
        background: true,
        autoplay: true,
        loop: true,
        muted: true,
        controls: false,
        byline: false,
        title: false,
        portrait: false
      }
      
      // For unlisted videos, use URL with hash parameter
      if (vimeoData.hashParam) {
        playerOptions.url = `https://player.vimeo.com/video/${activeVimeoId}?h=${vimeoData.hashParam}&dnt=1`
      } else {
        playerOptions.id = activeVimeoId
        playerOptions.dnt = true
      }
      
      const player = new (window as unknown as { Vimeo: { Player: new (el: HTMLElement, opts: unknown) => VimeoPlayer } }).Vimeo.Player(containerRef.current, playerOptions) as VimeoPlayer

      playerRef.current = player

      // Listen for video events
      player.on('loaded', () => {
        setVideoLoaded(true)
      })
      player.on('play', () => {
        setVideoPlaying(true)
      })

      // Wait for player to be ready
      await player.ready()
      
      // For mobile Safari, add a small delay before attempting to play
      const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent)
      const playDelay = isMobileSafari ? 300 : 0
      
      setTimeout(async () => {
        // Manually trigger play for unlisted videos (autoplay might be blocked)
        try {
          await player.play()
        } catch {
          // On Safari mobile, autoplay might fail - set up user interaction fallback
          if (isMobileSafari) {
            const playOnInteraction = async () => {
              try {
                await player.play()
                document.removeEventListener('touchstart', playOnInteraction)
                document.removeEventListener('click', playOnInteraction)
              } catch {
                // Ignore play errors
              }
            }
            
            document.addEventListener('touchstart', playOnInteraction, { once: true })
            document.addEventListener('click', playOnInteraction, { once: true })
          }
          
          // Still call setVideoPlaying to show the video even if autoplay fails
          setVideoPlaying(true)
        }
      }, playDelay)
      
    } catch {
      // Fallback: show video after a delay
      setTimeout(() => {
        setVideoLoaded(true)
        setVideoPlaying(true)
      }, 2000)
    }
  }, [isVimeoAPILoaded, loadVimeoAPI, activeVimeoId, vimeoData.hashParam])

  // Load Vimeo API when component mounts
  useEffect(() => {
    if (activeVimeoId) {
      loadVimeoAPI()
    }
  }, [loadVimeoAPI, activeVimeoId])

  // Initialize Vimeo player when API is loaded
  useEffect(() => {
    if (isVimeoAPILoaded && activeVimeoId) {
      initializeVimeoPlayer()
    }
  }, [isVimeoAPILoaded, activeVimeoId, initializeVimeoPlayer])

  // Clean up player when vimeo ID changes
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch {
          // Ignore destroy errors
        }
        playerRef.current = null
        setVideoLoaded(false)
        setVideoPlaying(false)
      }
    }
  }, [activeVimeoId])

  // Cleanup Vimeo player on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch {
          // Ignore destroy errors
        }
        playerRef.current = null
      }
    }
  }, [])

  // Animation trigger: only after preloader is completely finished
  useEffect(() => {
    if (isAdminMode) {
      setShowSvg(true) // Show immediately in admin
      return
    }

    // Only trigger when preloader state is 'hidden' and we're not on initial load
    if (preloaderState === 'hidden' && !isInitialLoad) {

      setShowSvg(true)
    }
  }, [isAdminMode, preloaderState, isInitialLoad])

  // Smooth scroll to next section function with Lenis integration
  const scrollToNextSection = useCallback(() => {
    // Try to use Lenis first for smooth scrolling
    
    // Find the next section after the hero
    const heroSection = document.querySelector('section')
    if (heroSection) {
      const nextSection = heroSection.nextElementSibling as HTMLElement
      if (nextSection) {
        // Try Lenis first
        const lenisSuccess = scrollToWithLenis(nextSection, { duration: 1.5 })
        
        // Fallback to custom animation if Lenis is not available
        if (!lenisSuccess) {
          const startPosition = window.pageYOffset
          const targetPosition = nextSection.offsetTop
          const distance = targetPosition - startPosition
          const duration = 1500 // 1.5 seconds for a much slower scroll
          let startTime: number | null = null

          // Easing function for smooth animation (easeInOutCubic)
          const easeInOutCubic = (t: number): number => {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
          }

          const animation = (currentTime: number) => {
            if (startTime === null) startTime = currentTime
            const timeElapsed = currentTime - startTime
            const progress = Math.min(timeElapsed / duration, 1)
            
            const easedProgress = easeInOutCubic(progress)
            const currentPosition = startPosition + (distance * easedProgress)
            
            window.scrollTo(0, currentPosition)
            
            if (progress < 1) {
              requestAnimationFrame(animation)
            }
          }

          requestAnimationFrame(animation)
        }
      }
    }
  }, [])

  return (
    <section className={styles.hero}>
      {/* Render video if we have a vimeo ID */}
      {activeVimeoId && (
        <motion.div 
          ref={containerRef}
          className={styles['hero__vimeo-wrapper']}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: isAdminMode ? 1 : (shouldShowVideo ? 1 : 0) }}
          transition={{ 
            duration: 1.0,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          title="Hero Video"
        />
      )}

    
      <div className={styles.hero__arrow} onClick={scrollToNextSection} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToNextSection(); } }} aria-label="Scroll to next section">
        <motion.svg 
          width="14" 
          height="14" 
          viewBox="0 0 14 14" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          animate={{ y: [0, 3, -3] }}
          transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
        >
          <path d="M7.00007 2.53571V11.4643M7.00007 11.4643L10.3483 8.11606M7.00007 11.4643L3.65186 8.11606" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </motion.svg>
      </div>

      <div className={styles.hero__overlay}></div>
      
      <div className={styles.hero__container}>
        <div className={styles.hero__content}>
          {svgCode && (
            <motion.div 
              className={styles.hero__svg}
              initial={{ opacity: isAdminMode ? 1 : 0 }}
              animate={{ 
                opacity: showSvg ? 1 : 0 
              }}
              transition={{ 
                duration: 0.8,
                delay: isAdminMode ? 0 : 0.1,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeSvg(svgCode) }}
            />
          )}
        </div>
      </div>
    </section>
  )
} 